import {HistoryFileMetadata} from "@/services/s3-storage/s3-storage";
import "core-js/actual/object/group-by";

export type HistoryMetadataGroup = {
  filename: string;
  metadatum: HistoryFileMetadata[];
};

type FileTypeMap<TKey extends string, TValue> = {
  [K in TKey]: TValue;
};

type ConvertFileTypeBase = "Combined" | "Instrumental" | "Original";
type ConvertFileKeyBase = "combinedUrl" | "instrumentalUrl" | "originalUrl";

export type ConvertFileType = ConvertFileTypeBase | "Vocal";
type ConvertFileKey = ConvertFileKeyBase | "vocalUrl";

export const convertFileTypeMap: FileTypeMap<ConvertFileType, ConvertFileKey> =
  {
    Combined: "combinedUrl",
    Instrumental: "instrumentalUrl",
    Vocal: "vocalUrl",
    Original: "originalUrl",
  };

export type DuetFileType = ConvertFileTypeBase;
type DuetFileKey = ConvertFileKeyBase;

export const duetFileTypeMap: FileTypeMap<DuetFileType, DuetFileKey> = {
  Combined: "combinedUrl",
  Instrumental: "instrumentalUrl",
  Original: "originalUrl",
};

export type StemSplitterFileType =
  | "Guitar"
  | "Piano"
  | "Drums"
  | "Bass"
  | "Vocal"
  | "Other";
type StemSplitterFileKey =
  | "guitarUrl"
  | "pianoUrl"
  | "drumsUrl"
  | "bassUrl"
  | "vocalsUrl"
  | "otherUrl";

export const stemSplitterFileTypeMap: FileTypeMap<
  StemSplitterFileType,
  StemSplitterFileKey
> = {
  Guitar: "guitarUrl",
  Piano: "pianoUrl",
  Drums: "drumsUrl",
  Bass: "bassUrl",
  Vocal: "vocalsUrl",
  Other: "otherUrl",
};

export type VocalRemoverFileType = "Vocal" | "Instrumental";
type VocalRemoverFileKey = "vocalUrl" | "instrumentalUrl";

export const vocalRemoverFileTypeMap: FileTypeMap<
  VocalRemoverFileType,
  VocalRemoverFileKey
> = {
  Vocal: "vocalUrl",
  Instrumental: "instrumentalUrl",
};

export type TtsFileType = "Text To Speech";
export type TtmFileType = "Text To Music";

const getConvertTypeFromTitle = (title: string): ConvertFileType => {
  const shortedType = title.split("_")[0];
  switch (shortedType) {
    case "conv":
      return "Combined";
    case "inst":
      return "Instrumental";
    case "voc":
      return "Vocal";
    case "orig":
      return "Original";
    default:
      throw new Error(`Unknown convert file type ${title}`);
  }
};

const getDuetTypeFromTitle = (title: string): DuetFileType => {
  const shortedType = title.split("_")[0];
  switch (shortedType) {
    case "conv":
      return "Combined";
    case "inst":
      return "Instrumental";
    case "orig":
      return "Original";
    default:
      throw new Error(`Unknown duet file type ${title}`);
  }
};

const getStemTypeFromTitle = (title: string): StemSplitterFileType => {
  const shortedType = title.split("_")[0];
  switch (shortedType) {
    case "guitar":
      return "Guitar";
    case "piano":
      return "Piano";
    case "drums":
      return "Drums";
    case "bass":
      return "Bass";
    case "vocals":
      return "Vocal";
    case "other":
      return "Other";
    default:
      throw new Error(`Unknown stem splitter file type ${title}`);
  }
};

const getVocalRemoverTypeFromTitle = (title: string): VocalRemoverFileType => {
  const shortedType = title.split("_")[0];
  switch (shortedType) {
    case "vocal":
    case "vocals":
      return "Vocal";
    case "instrumental":
      return "Instrumental";
    default:
      throw new Error(`Unknown vocal remover file type ${title}`);
  }
};

const splitFileName = (
  filename: string,
): { name: string; extension: string } => {
  const lastDotIndex = filename.lastIndexOf(".");

  if (lastDotIndex === -1) {
    return {name: filename, extension: ""};
  }

  const name = filename.substring(0, lastDotIndex);
  const extension = filename.substring(lastDotIndex + 1);

  return {name, extension};
};

const getNormalizedTitle = (title: string): string => {
  const firstUnderscoreIndex = title.indexOf("_");
  if (firstUnderscoreIndex === -1) {
    return title;
  }

  const secondUnderscoreIndex = title.indexOf("_", firstUnderscoreIndex + 1);
  if (secondUnderscoreIndex === -1) {
    return title.substring(firstUnderscoreIndex + 1);
  }

  return title.substring(secondUnderscoreIndex + 1);
};

const getIdentifier = (title: string) => {
  let currentUnderscoreIndex: number | null = null;
  let currentValue: string | null = null;

  do {
    const startIndex = currentUnderscoreIndex ? currentUnderscoreIndex + 1 : 0;

    const nextUnderscoreIndex = title.indexOf("_", startIndex);

    if (nextUnderscoreIndex === -1) {
      return "";
    }

    const value = title.substring(startIndex, nextUnderscoreIndex);

    currentUnderscoreIndex = nextUnderscoreIndex;

    // для тех ситуаций, когда по каким-либо причинам подряд идут два типа: {type}_{type}_{id}_{file_name}
    if (currentValue === value) {
      continue;
    }

    if (currentValue) {
      return value;
    }

    currentValue = value;
  } while (true);
};

const getVoiceName = (title: string): string => {
  const match = title.match(/\(([^)]+)\)/);
  if (match) {
    const textInBrackets = match[1];
    return textInBrackets.replace(" Ver", "");
  }
  return "";
};

const getBaseUrl = (url: string): string => {
  const urlFormat = new URL(url);
  return urlFormat.origin + urlFormat.pathname;
};

export const getDownloadFileTitle = (fileName: string, type?: string) => {
  const normalizedTitle = getNormalizedTitle(fileName);

  let genType: string | null = null;

  try {
    genType = type ?? getConvertTypeFromTitle(fileName);
  } catch (err) {
    console.log(err);
  }

  const {name, extension} = splitFileName(normalizedTitle);

  return genType ? `${name} - ${genType}.${extension}` : `${name}.${extension}`;
};

export const getFileNameFromUrl = (url: string, type?: string) => {
  const name = url.split("/");
  return decodeURIComponent(getDownloadFileTitle(name[name.length - 1], type));
};

const groupMetadata = (
  metadatum: HistoryFileMetadata[],
): HistoryMetadataGroup[] => {
  const groupedMetadatum = Object.groupBy(metadatum, (metadata, index) =>
    getIdentifier(metadata.name),
  );

  return Object.keys(groupedMetadatum)
    .filter((id) => id !== "")
    .map((id) => ({
      filename: getNormalizedTitle(groupedMetadatum[id]![0].name),
      metadatum: groupedMetadatum[id]!,
    }));
};

export {
  getConvertTypeFromTitle,
  getDuetTypeFromTitle,
  getStemTypeFromTitle,
  getVocalRemoverTypeFromTitle,
  getNormalizedTitle,
  getVoiceName,
  getBaseUrl,
  groupMetadata,
};
